import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { InvoiceService } from 'src/app/services/invoicing/invoice.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { QuotationService } from 'src/app/services/preliminaries/quotation.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-revert-import-quotations',
  templateUrl: './revert-import-quotations.component.html',
  styleUrl: './revert-import-quotations.component.scss',
  standalone: false,
})
export class RevertImportQuotationsComponent {

  private readonly branchService = inject(BranchService);
  private readonly companyService = inject(CompanyService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly invoiceService = inject(InvoiceService);
  private readonly quotationService = inject(QuotationService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  typeId: number = 0;
  branchId = 0;
  companyId: number | null = null;
  errorRevertOrder: number = 0;
  formReverse: FormGroup = new FormGroup({});
  listBranches: any[] = [];
  listCompanies: any[] = [];
  listTypes: any[] = [];
  revertOrdersAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>;
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  visibleTxt: boolean = false;

  async ngOnInit() {
    this.validateForm();
    await this.getAllLists();
  }

  downloadErrorsTxt() {
    if (!this.revertOrdersAlerts || this.revertOrdersAlerts.length === 0) {
      this.toastHandlerService.showToast('error', 'No hay alertas para exportar');
      return;
    }

    this.invoiceService.exportImportErrors(this.revertOrdersAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresReversiónImportacionCotizaciones_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast('error', 'Error al exportar el archivo');
      }
    });
  }

  async getAllLists() {
    try {
      const [companies, types] = await Promise.all([
        firstValueFrom(this.companyService.getAllCompaniesForSelectAsync()),
        firstValueFrom(this.quotationService.getAllTypesQuotationsForSelect(),)
      ]);
      this.listCompanies = companies;
      this.listTypes = types;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getBranch(branchId: number) {
    if (branchId != null) {
      this.branchId = branchId;
    } else {
      this.branchId = 0;
    }
  }

  async getCompany(companyId: number | null) {
    if (companyId != null && companyId != 0) {
      this.companyId = companyId;
      this.listBranches = await firstValueFrom(
        this.branchService.GetAllBranchesByCompanyIdForSelectAsync(this.companyId)
      );
    } else {
      this.companyId = null;
      this.listBranches = [];
    }
  }

  async getType(typeId: number) {
    if (typeId != null && typeId != 0) {
      this.typeId = typeId;
    } else {
      this.typeId = 0;
      this.formReverse.controls['typeId'].patchValue(0);
    }
  }

  async revertImportPendingsOrders() {
    if (this.formReverse.valid) {
      const accepted = await this.confirmDialogService.confirm(
        "¿Realmente desea revertir la importación de Cotizaciones?"
      );
      if (accepted) {
        try {
          const response = await firstValueFrom(
            this.quotationService.revertImportQuotation(this.branchId, this.typeId)
          );
          this.loadPreInvoicingModel = response;
          this.revertOrdersAlerts = response?.alerts ?? [];
          this.errorRevertOrder = response?.preInvoicingStateId;
          if (this.errorRevertOrder === 1) {
            this.visibleTxt = true;
            this.toastHandlerService.showToast(
              'error',
              'La reversión terminó con errores, puede exportarlos si lo desea',
            );
          } else if (this.revertOrdersAlerts.length > 0) {
            this.visibleTxt = true;
            this.toastHandlerService.showToast(
              'error',
              'La reversión terminó con alertas, puede exportarlos si lo desea',
            );
          } else {
            this.visibleTxt = false;
            this.toastHandlerService.showToast(
              'success',
              'La reversión de la importación de Cotizaciones se realizó exitosamente'
            );
          }
        } catch (error) {
          console.error('Error al revertir la importación de Cotizaciones.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      companyId: {
        required: 'La compañía es obligatiria.',
        min: 'La compañía es obligatiria.',
      },
      branchId: {
        required: 'La sucursal es obligatiria.',
        min: 'La sucursal es obligatiria.'
      },
      typeId: {
        required: 'El tipo es obligatirio.',
        min: 'El tipo es obligatirio.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formReverse.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formReverse = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      branchId: [0, [Validators.required, Validators.min(1)]],
      typeId: [0, [Validators.required, Validators.min(1)]]
    });
  }
}
